import * as THREE from 'three';
import type { Viewport } from '../scene/viewport.ts';
import { state, notify, type CameraView } from '../state.ts';
import { toCanonical } from '../retarget/retarget.ts';
import { evalCamera, evalProgram, type AimResolver, type CameraSample } from './evaluate.ts';
import { findCamera, type CameraAim } from './types.ts';

/** The subset of an actor entry the aim resolver needs (supplied by main.ts). */
export interface AimActor {
  id: string;
  kind: 'skeleton' | 'prop';
  bones: THREE.Object3D[];
  hips: THREE.Object3D | null;
}

export interface CameraDirectorDeps {
  getAimActors: () => AimActor[];
  /** Current playhead in seconds (smooth mixer time, not quantized frames). */
  getSeconds: () => number;
  /** Called after the director writes the viewport camera (euler readout etc.). */
  onApplied?: () => void;
}

export interface CameraDirector {
  /**
   * Per-frame hook for the render loop: replaces the unconditional
   * controls.update() — orbit controls own the camera only in Free view.
   */
  tick: () => void;
  /** Evaluate the active animated view at `seconds` and write the viewport camera. */
  applyToViewport: (seconds: number) => void;
  /** Switch between 'free' | 'program' | a camera id, snapshotting the free camera. */
  setView: (view: CameraView) => void;
  /** Evaluate a view without touching the viewport (viz, exports). */
  sample: (view: CameraView, seconds: number) => CameraSample | null;
  /** Resolve a tracking aim to a world point (null → keyframed target is used). */
  resolveAim: AimResolver;
}

/**
 * Drives the shared viewport camera from the camera program. Owns the
 * free-camera snapshot and the OrbitControls enable/disable handoff; writes
 * position/orientation/fov only — aspect stays owned by the viewport.
 */
export function createCameraDirector(vp: Viewport, deps: CameraDirectorDeps): CameraDirector {
  const scratch: CameraSample = {
    position: new THREE.Vector3(),
    target: new THREE.Vector3(),
    fov: 45,
  };
  const boneScratch = new THREE.Vector3();

  /** The free (orbit) camera pose, saved when switching to an animated view. */
  let freeSnapshot: { position: THREE.Vector3; target: THREE.Vector3; fov: number } | null = null;

  const resolveAim: AimResolver = (aim: CameraAim): THREE.Vector3 | null => {
    const actors = deps.getAimActors();
    switch (aim.mode) {
      case 'path':
        return null;
      case 'point':
        return boneScratch.set(aim.point[0], aim.point[1], aim.point[2]);
      case 'bone': {
        const actor = actors.find((a) => a.id === aim.actorId);
        if (!actor) return null;
        const bone =
          actor.bones.find((b) => toCanonical(b.name) === aim.bone) ??
          actor.hips ??
          actor.bones[0];
        if (!bone) return null;
        bone.updateWorldMatrix(true, false);
        boneScratch.setFromMatrixPosition(bone.matrixWorld);
        boneScratch.y += aim.heightOffset;
        return boneScratch;
      }
      case 'group': {
        const wanted =
          aim.actorIds === 'all'
            ? actors.filter((a) => a.kind === 'skeleton')
            : actors.filter((a) => (aim.actorIds as string[]).includes(a.id));
        if (wanted.length === 0) return null;
        boneScratch.set(0, 0, 0);
        let counted = 0;
        const p = new THREE.Vector3();
        for (const actor of wanted) {
          const anchor = actor.hips ?? actor.bones[0];
          if (!anchor) continue;
          anchor.updateWorldMatrix(true, false);
          boneScratch.add(p.setFromMatrixPosition(anchor.matrixWorld));
          counted++;
        }
        if (counted === 0) return null;
        return boneScratch.divideScalar(counted);
      }
    }
  };

  function sample(view: CameraView, seconds: number): CameraSample | null {
    if (view === 'free') return null;
    if (view === 'program') return evalProgram(state.cameras, seconds, resolveAim, scratch);
    const def = findCamera(state.cameras, view);
    if (!def) return null;
    return evalCamera(def, seconds, resolveAim, scratch);
  }

  function applyToViewport(seconds: number): void {
    const s = sample(state.cameraView, seconds);
    if (!s) return;
    const cam = vp.camera;
    cam.position.copy(s.position);
    cam.up.set(0, 1, 0);
    cam.lookAt(s.target);
    if (cam.fov !== s.fov) {
      cam.fov = s.fov;
      cam.updateProjectionMatrix();
    }
    deps.onApplied?.();
  }

  function snapshotFree(): void {
    freeSnapshot = {
      position: vp.camera.position.clone(),
      target: vp.controls.target.clone(),
      fov: vp.camera.fov,
    };
  }

  function restoreFree(): void {
    if (!freeSnapshot) return;
    vp.camera.position.copy(freeSnapshot.position);
    vp.controls.target.copy(freeSnapshot.target);
    if (vp.camera.fov !== freeSnapshot.fov) {
      vp.camera.fov = freeSnapshot.fov;
      vp.camera.updateProjectionMatrix();
    }
    vp.controls.update();
  }

  function setView(view: CameraView): void {
    // A deleted camera id (or an empty program) falls back to Free.
    if (view !== 'free' && view !== 'program' && !findCamera(state.cameras, view)) view = 'free';
    if (view === 'program' && state.cameras.cameras.length === 0) view = 'free';
    if (view === state.cameraView) return;

    const wasFree = state.cameraView === 'free';
    if (wasFree && view !== 'free') snapshotFree();
    state.cameraView = view;
    vp.controls.enabled = view === 'free';
    if (view === 'free') {
      restoreFree();
      deps.onApplied?.();
    } else {
      applyToViewport(deps.getSeconds());
    }
    notify();
  }

  function tick(): void {
    if (state.cameraView === 'free') {
      vp.controls.update();
    } else {
      applyToViewport(deps.getSeconds());
    }
  }

  return { tick, applyToViewport, setView, sample, resolveAim };
}
